import React, { forwardRef, Ref, useState } from 'react';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import { RouterUrlParams } from 'App';
import { useScope } from 'contexts/scopeContext';
import { ProgramWithLanding } from 'types/Program';
import { LandingPageFormsAvailability } from 'types/LandingPageFormsAvailability';
import WelcomeMessage from 'components/landingPages/components/WelcomeMessage';
import UserSettings from 'components/_common/UserSettings';
import ChangeOrganization from 'components/_common/panel/ChangeOrganization';
import { fixedT } from 'locale';
import { useAuth } from 'contexts/authContext';
import { border, colors } from 'theme';
import { Tooltip } from 'components/_new/Tooltip/Tooltip';
import { classNames } from 'primereact/utils';
import MobileUserSettings from 'components/_common/MobileUserSettings';
import { Dialog } from 'components/_new/Dialog';
import { useNavigate } from 'hooks/useNavigate';
import { ChatButton } from 'components/chat/ChatButton';
import { useBrandColors } from 'hooks/useBrandColors';
import { Helmet } from 'react-helmet';
import GuestMenu from '../../_common/GuestMobileSettings';
import { getBrowserLanguage } from '../../../utils/getBrowserLanguage';

interface TopbarProps {
	programData: ProgramWithLanding;
	formsAvailability?: LandingPageFormsAvailability;
}

const Topbar = forwardRef(({ programData, formsAvailability }: TopbarProps, ref: Ref<HTMLDivElement>) => {
	const { organizationName, programName } = useParams() as RouterUrlParams;
	const navigate = useNavigate();
	const location = useLocation();
	const { currentUser } = useAuth();
	const [showOrgDialog, setShowOrgDialog] = useState(false);
	const { applicationsData } = useScope();
	const hasAcceptedApplication = applicationsData.some(({ approved }) => approved === 'accepted');

	const { displayName, subtitle, logotype, brandColor } = programData;

	const { mainColor, textOnMainColor, lighterBackground, textOnLighterBackground, textColor } =
		useBrandColors(brandColor);

	const currentLanguage = currentUser
		? currentUser.language || programData.language
		: getBrowserLanguage() || programData.language;

	const baseLink = 'name' in programData ? programData.name : organizationName;
	const targetPath = `/${organizationName}/${programName}`;
	const isCurrentPath = location.pathname === targetPath;

	const style = `
			.lp .lp_hero_wrapper, .lp .lp_blue_wrapper {
				background-color: ${lighterBackground};
			}
			.lp .lp_hero_header_mark, .lp .lp_header_sub.lp_header_sub_white {
				color: ${textOnLighterBackground}  !important;
			}
			.lp .lp_hero_wrapper_inner, .lp .lp_hero_claim {
				color: ${textOnMainColor} !important;
			}
			.lp .lp_features_header, .lp .lp_intro_header, .lp .lp_header_sub {
				color: ${mainColor} !important;
			}
			.nav-active-link, .bg-dark-purple-blue {
				background-color: ${lighterBackground};
				color: ${textOnMainColor} !important;
			}
			.avatar, .guest-menu-button {
				background-color: ${lighterBackground} !important;
				color: ${textColor} !important;
			}
			.avatar .avatar-initials {
				color: ${textOnMainColor} !important;
			}
			.button-on-purple {
				background-color: ${textOnLighterBackground} !important;
				color: ${lighterBackground} !important;
			}
			.text-pink {
				color: ${textOnLighterBackground} !important;
			}
			.lp_blue_text {
				color: ${textOnMainColor} !important;
			}
			.lp .lp_blue_inner_header {
				color: ${textOnMainColor} !important;
			}
			.lp .lp_features_text a, .lp .lp_intro_inner a {
				color: ${mainColor} !important;
			}
			.bg-primary {
				background-color: ${mainColor} !important;
				color: ${textOnMainColor} !important;
			}
		`;

	return (
		<>
			<Helmet>
				<style type="text/css">{style}</style>
			</Helmet>
			<div ref={ref} className="w-full bg-white" style={{ zIndex: 100 }}>
				<div className="flex justify-content-between landing-page-container py-3">
					<div className="max-h-3rem min-w-[48px]">
						{' '}
						{/* dodana minimalna szerokość */}
						{logotype && (
							<img
								src={logotype}
								alt="logo"
								onClick={() => navigate(`/${organizationName}/${programName}`)}
								className={classNames('max-h-3rem', { 'cursor-pointer': !isCurrentPath })}
							/>
						)}
					</div>
					<div className="hidden sm:flex flex-column text-right">
						<h3 className="mt-0 mb-1">{displayName}</h3>
						<small>{subtitle}</small>
					</div>
				</div>

				<div
					style={{ borderBottom: border(colors.lightGray), backgroundColor: mainColor, color: textColor }}
					className="z-1"
				>
					<div className="flex justify-content-between landing-page-container py-0">
						<div className="hidden md:flex align-items-center column-gap-4">
							<NavLink
								to={`/${baseLink}`}
								className={({ isActive }) =>
									classNames('text-white font-bold text-sm p-3', {
										'bg-dark-purple-blue': isActive,
										'nav-active-link': isActive,
									})
								}
								end
							>
								{fixedT(currentLanguage, 'misc.aboutProgram')}
							</NavLink>
							{formsAvailability?.isMentorRecruitmentAvailable && (
								<>
									{!currentUser && <Tooltip target=".link" position="bottom" />}
									<span
										className="hidden md:inline-flex link"
										data-pr-tooltip={fixedT(currentLanguage, 'landingPages.linkTooltip')}
										id="text-white font-bold text-sm p-3"
									>
										<NavLink
											to={`/${baseLink}/apply-mentor`}
											className={({ isActive }) =>
												classNames('text-white font-bold text-sm p-3', {
													'bg-dark-purple-blue': isActive,
												})
											}
										>
											{fixedT(currentLanguage, 'landingPages.buttons.mentorApplication')}
										</NavLink>
									</span>
								</>
							)}
							{formsAvailability?.isMenteeRecruitmentAvailable && (
								<>
									{!currentUser && <Tooltip target=".link" position="bottom" />}
									<span
										className="hidden md:inline-flex link"
										data-pr-tooltip={fixedT(currentLanguage, 'landingPages.linkTooltip')}
										id="text-white font-bold text-sm p-3"
									>
										<NavLink
											to={`/${baseLink}/apply-mentee`}
											className={({ isActive }) =>
												classNames('text-white font-bold text-sm p-3', {
													'bg-dark-purple-blue': isActive,
												})
											}
										>
											{fixedT(currentLanguage, 'landingPages.buttons.menteeApplication')}
										</NavLink>
									</span>
								</>
							)}
							{currentUser && hasAcceptedApplication && (
								<span
									className="hidden md:inline-flex link"
									data-pr-tooltip={fixedT(currentLanguage, 'landingPages.linkTooltip')}
									id="text-white font-bold text-sm p-3"
								>
									<NavLink
										to={`/panel/${programData.name}`}
										className={classNames('text-white font-bold text-sm p-3')}
									>
										{fixedT(currentLanguage, 'landingPages.buttons.userArea')}
									</NavLink>
								</span>
							)}
						</div>

						<div className="flex align-items-center column-gap-4 py-2 welcome-message">
							<WelcomeMessage pageDetails={programData} formsAvailability={formsAvailability} />

							{currentUser && (
								<div className="hidden md:inline-flex gap-2">
									<ChatButton landingMode />
									<UserSettings
										currentUser={currentUser}
										setShowOrgDialog={setShowOrgDialog}
										source="landingPage"
										optionsLanguage={currentLanguage}
										hasApplicationSubmitted={!!applicationsData?.length}
									/>
								</div>
							)}
						</div>

						<div className="flex flex-row flex-nowrap align-items-center column-gap-4 py-2 md:hidden gap-2 rside">
							{currentUser && (
								<>
									<ChatButton landingMode />
									<MobileUserSettings
										currentUser={currentUser}
										setShowOrgDialog={setShowOrgDialog}
										source="landingPage"
										optionsLanguage={currentLanguage}
										hasApplicationSubmitted={!!applicationsData?.length}
										formsAvailability={formsAvailability}
									/>
								</>
							)}
							{!currentUser && (
								<GuestMenu programData={programData} formsAvailability={formsAvailability} />
							)}
						</div>
					</div>
				</div>

				<Dialog
					title="Zmiana organizacji"
					size="sm"
					onHide={() => setShowOrgDialog(false)}
					visible={showOrgDialog}
				>
					<ChangeOrganization
						defaultSelected={
							organizationName && programName
								? {
										organization: organizationName,
										program: programName,
								  }
								: undefined
						}
						card
						onCancel={() => setShowOrgDialog(false)}
					/>
				</Dialog>
			</div>
		</>
	);
});

export default Topbar;
